import { Component } from '@theme/component';

/**
 * Discover hero carousel — stacked-slide carousel driven by the Web
 * Animations API, mirroring the homepage hero architecture (hero.js).
 *
 * Slides stack absolutely inside the viewport; exactly one slide is
 * active at a time (is-active). Navigation wraps with plain modulo
 * arithmetic — no clones, no transform track, no transitionend state
 * machine — so rapid clicking, autoplay, touch swipes, and keyboard
 * input cancel any in-flight animation cleanly instead of racing
 * against pending jump bookkeeping.
 */
class DiscoverHeroComponent extends Component {
  #slides = [];
  #activeIndex = 0;
  #delay = 6500;
  #reducedMotion = false;
  #autoplayPaused = false;
  #resumeTimer = null;
  #resumeDelay = 15000;
  #timer = null;
  #advanceGen = 0;
  #enterAnim = null;
  #leaveAnim = null;
  #animSlide = null;
  #animPrev = null;
  #current;
  #total;
  #prevBtn;
  #nextBtn;
  #autoplayBtn;
  #progress;
  #visibilityObserver = null;
  #isVisible = true;
  #fillEl = null;

  #onPrevClick = null;
  #onNextClick = null;
  #onAutoplayClick = null;

  #touchViewport = null;
  #onTouchStart = null;
  #onTouchMove = null;
  #onTouchEnd = null;

  /* ---------- debug instrumentation (opt-in via ?dhr_debug=1) ---------- */

  #debug = false;
  #debugPanel = null;
  #debugLogs = [];

  #log(source, detail = {}) {
    window.__dhrLog = window.__dhrLog || [];
    window.__dhrLog.push({
      t: performance.now().toFixed(1),
      source,
      detail,
    });
    if (!this.#debug) return;
    const entry = {
      t: (performance.now() / 1000).toFixed(2) + 's',
      source,
      active: this.#activeIndex,
      reduced: this.#reducedMotion,
      paused: this.#autoplayPaused,
      animRunning: !!(this.#enterAnim || this.#leaveAnim),
      detail,
    };
    this.#debugLogs.unshift(entry);
    if (this.#debugLogs.length > 60) this.#debugLogs.length = 60;
    this.#renderDebugPanel();
  }

  #setupDebug() {
    if (!this.#debug) return;
    this.#debugPanel = document.createElement('div');
    this.#debugPanel.id = 'dhr-debug-panel';
    this.#debugPanel.style.cssText =
      'position:fixed;right:1rem;bottom:1rem;z-index:99999;' +
      'max-width:420px;max-height:50vh;overflow:auto;' +
      'background:rgba(10,12,16,.92);color:#e8e8e8;' +
      'font:11px/1.5 ui-monospace,Menlo,Consolas,monospace;' +
      'padding:.6rem .8rem;border-radius:8px;' +
      'box-shadow:0 6px 24px rgba(0,0,0,.35);' +
      'pointer-events:none;';
    document.body.appendChild(this.#debugPanel);
    this.#renderDebugPanel();
  }

  #renderDebugPanel() {
    if (!this.#debugPanel) return;
    const lines = [
      'discover-hero debug',
      'slides=' + this.#slides.length + ' active=' + this.#activeIndex,
      'reduced=' + this.#reducedMotion + ' paused=' + this.#autoplayPaused,
      'anim=' + (!!this.#enterAnim || !!this.#leaveAnim) +
        ' timer=' + (!!this.#timer) +
        ' resume=' + (!!this.#resumeTimer),
      '--- events ---',
      ...this.#debugLogs.map((e) =>
        e.t + ' ' + e.source + ' a=' + e.active +
        (e.detail ? ' ' + JSON.stringify(e.detail) : '')
      ),
    ];
    this.#debugPanel.textContent = lines.join('\n');
  }

  connectedCallback() {
    super.connectedCallback();

    this.#debug =
      new URLSearchParams(window.location.search).has('dhr_debug');
    if (this.#debug
        || (window.__dhrLog === undefined && this.dataset.debug === '1')) {
      window.__dhrLog = window.__dhrLog || [];
    }
    this.#setupDebug();

    this.#slides = Array.from(
      this.querySelectorAll('[data-discover-hero-slide]')
    );
    if (this.#slides.length === 0) return;

    this.#reducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    this.#delay =
      parseInt(this.dataset.autoplayDelay || '6500', 10) || 6500;

    this.#current = this.querySelector('[data-discover-hero-current]');
    this.#total = this.querySelector('[data-discover-hero-total]');
    this.#prevBtn = this.querySelector('[data-discover-hero-prev]');
    this.#nextBtn = this.querySelector('[data-discover-hero-next]');
    this.#autoplayBtn = this.querySelector('[data-discover-hero-autoplay]');
    this.#progress = this.querySelector('[data-discover-hero-progress]');

    // Single-slide heroes have nothing to cycle between: no autoplay,
    // no controls, no progress. The one slide is already active.
    if (this.#slides.length < 2) {
      this.#updateDisplay();
      this.#log('start-single', { slides: this.#slides.length });
      return;
    }

    this.#bindControls();
    this.#bindKeyboard();
    this.#bindTouch();
    this.#bindVisibilityObserver();
    this.#updateDisplay();
    this.#startTimer();
    this.#log('start', { slides: this.#slides.length, reduced: this.#reducedMotion });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#clearTimer();
    this.#clearProgress();
    this.#cancelAnimations();
    clearTimeout(this.#resumeTimer);
    this.#visibilityObserver?.disconnect();
    this.#visibilityObserver = null;
    document.removeEventListener('keydown', this._onKeyDown);

    if (this.#prevBtn && this.#onPrevClick) {
      this.#prevBtn.removeEventListener('click', this.#onPrevClick);
      this.#onPrevClick = null;
    }
    if (this.#nextBtn && this.#onNextClick) {
      this.#nextBtn.removeEventListener('click', this.#onNextClick);
      this.#onNextClick = null;
    }
    if (this.#autoplayBtn && this.#onAutoplayClick) {
      this.#autoplayBtn.removeEventListener('click', this.#onAutoplayClick);
      this.#onAutoplayClick = null;
    }

    if (this.#touchViewport && this.#onTouchStart) {
      this.#touchViewport.removeEventListener('touchstart', this.#onTouchStart);
      this.#touchViewport.removeEventListener('touchmove', this.#onTouchMove);
      this.#touchViewport.removeEventListener('touchend', this.#onTouchEnd);
      this.#touchViewport = null;
      this.#onTouchStart = null;
      this.#onTouchMove = null;
      this.#onTouchEnd = null;
    }
  }

  /* ---------- controls ---------- */

  #bindControls() {
    if (this.#prevBtn) {
      this.#onPrevClick = () => {
        this.#log('prev-click');
        this.#goTo(this.#activeIndex - 1, true, 'backward');
        this.#prevBtn.blur();
      };
      this.#prevBtn.addEventListener('click', this.#onPrevClick);
    }
    if (this.#nextBtn) {
      this.#onNextClick = () => {
        this.#log('next-click');
        this.#goTo(this.#activeIndex + 1, true, 'forward');
        this.#nextBtn.blur();
      };
      this.#nextBtn.addEventListener('click', this.#onNextClick);
    }
    if (this.#autoplayBtn) {
      this.#onAutoplayClick = () => {
        this.#log('autoplay-click', { pausedWillBe: !this.#autoplayPaused });
        this.#autoplayPaused = !this.#autoplayPaused;
        this.#autoplayBtn.classList.toggle('stop', this.#autoplayPaused);
        const label = this.#autoplayPaused
          ? this.#autoplayBtn.dataset.playLabel
          : this.#autoplayBtn.dataset.pauseLabel;
        this.#autoplayBtn.setAttribute('aria-label', label || '');
        if (this.#autoplayPaused) {
          this.#clearTimer();
          this.#clearProgress();
        } else {
          this.#startTimer();
        }
      };
      this.#autoplayBtn.addEventListener('click', this.#onAutoplayClick);
    }
  }

  /* ---------- track navigation ---------- */

  #goTo(next, userInitiated = false, direction) {
    const len = this.#slides.length;
    next = ((next % len) + len) % len;

    // Bump the generation on EVERY navigation. This invalidates any
    // autoplay/resume timeout already queued in the macrotask queue:
    // without it, a click landing between the timeout's firing and its
    // callback execution would advance the carousel twice (the user's
    // click plus the stale autoplay callback), reading as a "jump".
    this.#advanceGen++;

    if (next === this.#activeIndex) {
      if (
        this.#fillEl === null &&
        this.#timer === null &&
        userInitiated
      ) {
        this.#scheduleResume();
      }
      this.#log('goto-same', { next, userInitiated, direction });
      return;
    }

    this.#clearTimer();
    this.#clearProgress();
    this.#cancelAnimations();

    const prevIndex = this.#activeIndex;
    const prev = this.#slides[prevIndex];
    this.#activeIndex = next;
    const slide = this.#slides[this.#activeIndex];

    const wrapForward = prevIndex === len - 1 && next === 0 && len > 2;
    const wrapBackward = prevIndex === 0 && next === len - 1 && len > 2;
    const forward =
      direction === 'forward'
        ? true
        : direction === 'backward'
          ? false
          : wrapForward
            ? true
            : wrapBackward
              ? false
              : next > prevIndex;

    const offScreen = forward ? '100%' : '-100%';
    const leaveEnd = forward ? '-100%' : '100%';
    const easing = 'cubic-bezier(0.2, 0, 0, 1)';
    const duration = this.#reducedMotion ? 0 : 800;

    slide.classList.add('is-active');
    slide.removeAttribute('hidden');
    slide.setAttribute('aria-hidden', 'false');
    slide.style.zIndex = '2';
    slide.style.pointerEvents = 'auto';

    prev.classList.remove('is-active');
    prev.removeAttribute('hidden');
    prev.setAttribute('aria-hidden', 'false');
    prev.style.zIndex = '1';
    prev.style.pointerEvents = 'none';

    if (typeof slide.animate === 'function') {
      const enterAnim = slide.animate(
        [
          { transform: 'translateX(' + offScreen + ')' },
          { transform: 'translateX(0)' },
        ],
        { duration, easing, fill: 'forwards' }
      );

      const leaveAnim = prev.animate(
        [
          { transform: 'translateX(0)' },
          { transform: 'translateX(' + leaveEnd + ')' },
        ],
        { duration, easing, fill: 'forwards' }
      );

      this.#enterAnim = enterAnim;
      this.#leaveAnim = leaveAnim;
      this.#animSlide = slide;
      this.#animPrev = prev;

      const cleanup = () => {
        if (this.#enterAnim !== enterAnim) return;

        enterAnim.cancel();
        leaveAnim.cancel();
        this.#enterAnim = null;
        this.#leaveAnim = null;
        this.#animSlide = null;
        this.#animPrev = null;

        this.#resetSlide(slide);
        this.#resetSlide(prev);
        prev.setAttribute('hidden', '');
        prev.setAttribute('aria-hidden', 'true');
      };

      const animDone = (anim) => {
        if (anim.finished) return anim.finished.catch(() => {});
        return new Promise((r) => { anim.onfinish = r; });
      };
      Promise.all([animDone(enterAnim), animDone(leaveAnim)]).then(cleanup);
    } else {
      this.#resetSlide(prev);
      this.#resetSlide(slide);
      prev.setAttribute('hidden', '');
      prev.setAttribute('aria-hidden', 'true');
    }

    this.#updateDisplay();

    if (userInitiated) {
      this.#scheduleResume();
      this.#log('goto-done', { next, direction, userInitiated: true });
    } else {
      this.#startTimer();
      this.#log('goto-done', { next, direction, autoplay: true });
    }
  }

  /**
   * Clears inline styles applied during a transition.
   * The active class is not touched: an in-flight enter slide keeps
   * is-active until cleanup finishes, and the leaving slide's class was
   * already removed in #goTo.
   */
  #resetSlide(slide) {
    if (!slide) return;
    slide.style.opacity = '';
    slide.style.transform = '';
    slide.style.transition = '';
    slide.style.zIndex = '';
    slide.style.pointerEvents = '';
    slide.style.visibility = '';
  }

  #updateDisplay() {
    const display = this.#activeIndex + 1;
    if (this.#current) {
      this.#current.textContent = String(display).padStart(2, '0');
    }
    if (this.#total) {
      this.#total.textContent = String(this.#slides.length).padStart(2, '0');
    }
  }

  /* ---------- keyboard ---------- */

  #bindKeyboard() {
    this._onKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        this.#log('key-left');
        this.#goTo(this.#activeIndex - 1, true, 'backward');
      }
      if (e.key === 'ArrowRight') {
        this.#log('key-right');
        this.#goTo(this.#activeIndex + 1, true, 'forward');
      }
    };
    document.addEventListener('keydown', this._onKeyDown);
  }

  /* ---------- touch swipe ---------- */

  #bindTouch() {
    const viewport = this.querySelector('.dhr-viewport');
    if (!viewport) return;

    this.#touchViewport = viewport;

    let touchX = 0;
    let touchY = 0;
    let touchDX = 0;
    let touchDY = 0;
    let touchOn = false;

    this.#onTouchStart = (e) => {
      if (e.target.closest('a, button, input')) return;
      touchX = e.touches[0].clientX;
      touchY = e.touches[0].clientY;
      touchDX = 0;
      touchDY = 0;
      touchOn = true;
    };
    this.#onTouchMove = (e) => {
      if (!touchOn) return;
      touchDX = e.touches[0].clientX - touchX;
      touchDY = e.touches[0].clientY - touchY;
      if (Math.abs(touchDX) > Math.abs(touchDY) * 1.15) {
        e.preventDefault();
      }
    };
    this.#onTouchEnd = () => {
      if (!touchOn) return;
      touchOn = false;
      if (
        Math.abs(touchDX) > 56 &&
        Math.abs(touchDX) > Math.abs(touchDY) * 1.15
      ) {
        this.#log('touch-swipe', { dx: Math.round(touchDX), dy: Math.round(touchDY) });
        this.#goTo(
          this.#activeIndex + (touchDX < 0 ? 1 : -1),
          true,
          touchDX < 0 ? 'forward' : 'backward'
        );
      }
    };

    viewport.addEventListener('touchstart', this.#onTouchStart, {
      passive: true,
    });
    viewport.addEventListener('touchmove', this.#onTouchMove, {
      passive: false,
    });
    viewport.addEventListener('touchend', this.#onTouchEnd);
  }

  /* ---------- autoplay + progress ---------- */

  #clearTimer() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }
  }

  #cancelAnimations() {
    if (this.#enterAnim) {
      this.#enterAnim.cancel();
      this.#enterAnim = null;
      this.#resetSlide(this.#animSlide);
      if (this.#animSlide) {
        this.#animSlide.setAttribute('hidden', '');
        this.#animSlide.setAttribute('aria-hidden', 'true');
      }
      this.#animSlide = null;
    }
    if (this.#leaveAnim) {
      this.#leaveAnim.cancel();
      this.#leaveAnim = null;
      this.#resetSlide(this.#animPrev);
      if (this.#animPrev) {
        this.#animPrev.setAttribute('hidden', '');
        this.#animPrev.setAttribute('aria-hidden', 'true');
      }
      this.#animPrev = null;
    }
  }

  #startTimer() {
    // Only clean up timer and progress — never cancel the slide
    // transition animations #goTo just created.
    this.#clearTimer();
    this.#clearProgress();

    // User-initiated pause stops autoplay entirely. Reduced motion,
    // however, must not freeze the carousel: keep the timer-driven
    // rotation working, only skip the animated progress fill (#goTo
    // already shortens slide transitions to 0ms under reduced motion).
    if (this.#autoplayPaused) {
      this.#log('startTimer-skip', { reduced: this.#reducedMotion, paused: true });
      return;
    }

    if (this.#reducedMotion) {
      this.#log('startTimer-reduced', { reduced: true });
    } else {
      this.#startProgress();
    }

    // Generation guard: if the user navigates before this timeout
    // fires, its callback must NOT advance the carousel again (the
    // queued macrotask survives clearTimeout — it would turn one
    // click into a two-slide jump).
    const gen = this.#advanceGen;
    this.#timer = setTimeout(() => {
      this.#timer = null;
      if (gen !== this.#advanceGen) {
        this.#log('autoplay-stale', { gen, pageGen: this.#advanceGen });
        return;
      }
      this.#log('autoplay-fire');
      this.#goTo(this.#activeIndex + 1, false, 'forward');
    }, this.#delay);
  }

  #scheduleResume() {
    clearTimeout(this.#resumeTimer);
    this.#clearProgress();
    const gen = this.#advanceGen;
    this.#resumeTimer = setTimeout(() => {
      this.#resumeTimer = null;
      if (gen !== this.#advanceGen) return;
      this.#log('resume-fire');
      this.#startTimer();
    }, this.#resumeDelay);
  }

  #startProgress() {
    if (!this.#progress) return;
    const fill = this.#progress;
    this.#fillEl = fill;

    fill.style.animation = 'none';
    void fill.offsetHeight;
    fill.style.animation =
      'dhr-progress-fill ' + this.#delay + 'ms linear forwards';

    if (!this.#isVisible) {
      fill.style.animationPlayState = 'paused';
    }
  }

  #clearProgress() {
    if (this.#fillEl) {
      this.#fillEl.style.animation = '';
      this.#fillEl.style.width = '0%';
      this.#fillEl = null;
    }
  }

  /* ---------- visibility observer ---------- */

  #bindVisibilityObserver() {
    if (!('IntersectionObserver' in window)) return;
    this.#visibilityObserver = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          this.#onVisible();
        } else {
          this.#onHidden();
        }
      },
      { threshold: 0 }
    );
    this.#visibilityObserver.observe(this);
  }

  #onHidden() {
    this.#isVisible = false;
    // Pause visual progress only — the setTimeout keeps running so
    // slide timing is unaffected when scrolled away.
    if (this.#fillEl) {
      this.#fillEl.style.animationPlayState = 'paused';
    }
  }

  #onVisible() {
    this.#isVisible = true;
    if (this.#fillEl) {
      this.#fillEl.style.animationPlayState = 'running';
    }
  }
}

if (!customElements.get('discover-hero-component')) {
  customElements.define(
    'discover-hero-component',
    DiscoverHeroComponent
  );
}